import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { PurchaseOrderApi, POSkuApi, SkuInspectionHistoryItem } from '../types';
import DatePicker from '../components/DatePicker';

const API_BASE = import.meta.env.VITE_API_BASE_URL;
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

const PODetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState<PurchaseOrderApi | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedSkus, setSelectedSkus] = useState<string[]>([]);
  const [users, setUsers] = useState<{ id: string; name: string; role: string; profileImage: string | null }[]>([]);
  const [isInspectorDropdownOpen, setIsInspectorDropdownOpen] = useState(false);
  const inspectorDropdownRef = useRef<HTMLDivElement>(null);
  const [isInspectionTypeDropdownOpen, setIsInspectionTypeDropdownOpen] = useState(false);
  const inspectionTypeDropdownRef = useRef<HTMLDivElement>(null);
  const [assignForm, setAssignForm] = useState({ user_id: '', inspection_type: '', due_date: '', special_instructions: '' });
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [historySku, setHistorySku] = useState<POSkuApi | null>(null);
  const [history, setHistory] = useState<SkuInspectionHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchPO = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/v1/purchase-orders/${id}`, { headers: getHeaders() });
      const data = await response.json();
      if (data.success) {
        setPo(data.data);
        setSelectedSkus(data.data.skus?.map((s: POSkuApi) => s.id) || []);
      }
    } catch (err) {
      console.error('Failed to fetch PO:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/users`, { headers: getHeaders() });
      const data = await response.json();
      if (data.success) setUsers(data.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  useEffect(() => {
    fetchPO();
    fetchUsers();
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inspectorDropdownRef.current && !inspectorDropdownRef.current.contains(event.target as Node)) {
        setIsInspectorDropdownOpen(false);
      }
      if (inspectionTypeDropdownRef.current && !inspectionTypeDropdownRef.current.contains(event.target as Node)) {
        setIsInspectionTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const nonAdminUsers = users.filter(u => u.role !== 'admin');
  const selectedInspector = nonAdminUsers.find(u => u.id === assignForm.user_id);

  const inspectionTypeOptions = [
    { value: 'final', label: 'Final Inspection' },
  ];
  const selectedInspectionType = inspectionTypeOptions.find(o => o.value === assignForm.inspection_type);

  const toggleSkuSelection = (skuId: string) => {
    setSelectedSkus(prev =>
      prev.includes(skuId) ? prev.filter(n => n !== skuId) : [...prev, skuId]
    );
  };

  const toggleAllSkus = () => {
    if (!po?.skus) return;
    if (selectedSkus.length === po.skus.length) {
      setSelectedSkus([]);
    } else {
      setSelectedSkus(po.skus.map(s => s.id));
    }
  };

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    setAssignError('');
    setAssigning(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/purchase-orders/${id}/assign`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          sku_ids: selectedSkus,
          user_id: assignForm.user_id,
          inspection_type: assignForm.inspection_type,
          due_date: assignForm.due_date || null,
          special_instructions: assignForm.special_instructions || null,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setIsAssignModalOpen(false);
        setAssignForm({ user_id: '', inspection_type: '', due_date: '', special_instructions: '' });
        fetchPO();
      } else {
        setAssignError(data.message || 'Failed to assign');
      }
    } catch (err) {
      setAssignError('Failed to assign. Please try again.');
    } finally {
      setAssigning(false);
    }
  };

  const handleEdit = () => {
    navigate(`/purchase-orders/edit/${po?.id}`);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const openHistory = async (sku: POSkuApi) => {
    setHistorySku(sku);
    setHistory([]);
    setHistoryLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/purchase-orders/${id}/skus/${sku.id}/inspection-history`, { headers: getHeaders() });
      const data = await response.json();
      if (data.success) setHistory(data.data);
    } catch (err) {
      console.error('Failed to fetch inspection history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-indigo-100 text-indigo-700',
      'bg-pink-100 text-pink-700',
      'bg-blue-100 text-blue-700',
      'bg-purple-100 text-purple-700',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const getUniqueInspectors = () => {
    const map = new Map<string, { id: string; name: string; profileImage: string | null }>();
    po?.assignments?.forEach(a => {
      if (a.inspector && !map.has(a.inspector.id)) map.set(a.inspector.id, a.inspector);
    });
    return Array.from(map.values());
  };

  const getInspectionStatuses = () => {
    const types = ['pilot', 'fabric', 'line', 'final', 'container'];
    const statuses: Record<string, string> = {};
    types.forEach(type => {
      const hasAssignment = po?.assignments?.some(a => a.inspection_type === type);
      statuses[type] = hasAssignment ? 'Scheduled' : 'Pending';
    });
    return statuses;
  };

  const getSkuAssignedTo = (skuId: string) => {
    const skuData = po?.skus?.find(s => s.id === skuId);
    if (!skuData?.assignments || skuData.assignments.length === 0) return 'Unassigned';
    return skuData.assignments.map(a => a.inspector?.name).filter(Boolean).join(', ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="material-symbols-outlined animate-spin text-[24px] text-slate-400">progress_activity</span>
        <span className="ml-2 text-slate-500">Loading...</span>
      </div>
    );
  }

  if (!po) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <span className="material-symbols-outlined text-[48px] mb-4">error_outline</span>
        <p>Purchase order not found.</p>
        <Link to="/purchase-orders" className="mt-4 text-[#136bce] hover:underline">Back to Purchase Orders</Link>
      </div>
    );
  }

  const inspectors = getUniqueInspectors();
  const inspectionStatuses = getInspectionStatuses();

  return (
    <div className="mx-auto w-full max-w-full">
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="flex mb-6 text-sm text-slate-500">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/dashboard" className="inline-flex items-center hover:text-slate-900 transition-colors">
              <span className="material-symbols-outlined text-[18px] mr-2">dashboard</span>
              Dashboard
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <span className="material-symbols-outlined text-slate-400 text-[18px]">chevron_right</span>
              <Link to="/purchase-orders" className="ml-1 hover:text-slate-900 md:ml-2 transition-colors">Purchase Orders</Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <span className="material-symbols-outlined text-slate-400 text-[18px]">chevron_right</span>
              <span className="ml-1 font-medium text-[#136bce] md:ml-2">{po.po_number}</span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Header section */}
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Purchase Order Details</h1>
          <p className="text-sm text-slate-500 mt-1">View details, SKUs and assign inspectors for {po.po_number}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleEdit}
            className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">edit</span>
            Edit PO
          </button>
          <button
            onClick={() => setIsAssignModalOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-[#136bce] px-6 py-2.5 text-sm font-medium text-white shadow-lg shadow-blue-500/20 hover:bg-blue-700 transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">person_add</span>
            Assign PO
          </button>
        </div>
      </div>

      {/* Basic Details Section */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm mb-8">
        <div className="border-b border-slate-200 px-6 py-4 flex items-center gap-2">
          <span className="material-symbols-outlined text-slate-400">info</span>
          <h2 className="text-base font-semibold text-slate-900">Basic Details</h2>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Purchase Order Number</span>
            <span className="text-lg font-bold text-slate-900">{po.po_number}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer Name</span>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center text-slate-600 font-bold text-xs">
                {getInitials(po.customer_name)}
              </div>
              <span className="text-base font-medium text-slate-900">{po.customer_name}</span>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Assigned To</span>
            <div className="flex -space-x-2 overflow-hidden mt-1">
              {inspectors.length > 0 ? inspectors.map(inspector => (
                <div
                  key={inspector.id}
                  className={`flex h-9 w-9 items-center justify-center rounded-full ring-2 ring-white text-xs font-bold cursor-help ${getAvatarColor(inspector.name)}`}
                  title={inspector.name}
                >
                  {getInitials(inspector.name)}
                </div>
              )) : (
                <span className="text-sm text-slate-400 italic">Unassigned</span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ship Date</span>
            <div className="flex items-center gap-2 text-slate-700 font-medium">
              <span className="material-symbols-outlined text-[18px] text-slate-400">event</span>
              {formatDate(po.ship_date)}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</span>
            <div>
              <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ring-1 ring-inset ${
                po.status === 'In Production' ? 'bg-amber-50 text-amber-700 ring-amber-600/20' :
                po.status === 'Completed' ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' :
                po.status === 'Shipped' ? 'bg-blue-50 text-blue-700 ring-blue-600/20' :
                'bg-slate-50 text-slate-600 ring-slate-300'
              }`}>
                {po.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Inspection Statuses Section */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm mb-8">
        <div className="border-b border-slate-200 px-6 py-4 flex items-center gap-2">
          <span className="material-symbols-outlined text-slate-400">fact_check</span>
          <h2 className="text-base font-semibold text-slate-900">Inspection Status</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { key: 'pilot', label: 'Pilot Inspection' },
              { key: 'fabric', label: 'Fabric Inspection' },
              { key: 'line', label: 'Line Inspection' },
              { key: 'final', label: 'Final Inspection' },
              { key: 'container', label: 'Container Inspection' }
            ].map((item) => {
              const status = inspectionStatuses[item.key] || 'Pending';
              return (
                <div key={item.key} className="flex flex-col p-4 rounded-xl border border-slate-100 bg-slate-50/50">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <div className={`h-2.5 w-2.5 rounded-full ${
                      status === 'Completed' || status === 'Passed' ? 'bg-emerald-500' :
                      status === 'In Progress' ? 'bg-amber-500 animate-pulse' :
                      status === 'Scheduled' ? 'bg-blue-500' :
                      status === 'Failed' ? 'bg-red-500' :
                      'bg-slate-300'
                    }`} />
                    <span className={`text-sm font-semibold ${
                      status === 'Completed' || status === 'Passed' ? 'text-emerald-700' :
                      status === 'In Progress' ? 'text-amber-700' :
                      status === 'Scheduled' ? 'text-blue-700' :
                      status === 'Failed' ? 'text-red-700' :
                      'text-slate-500'
                    }`}>
                      {status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* SKU Details Section */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col">
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-slate-400">inventory_2</span>
            <h2 className="text-base font-semibold text-slate-900">SKU Details</h2>
            <span className="ml-2 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">{po.skus?.length || 0} Items</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="w-4 px-6 py-3" scope="col">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-[#136bce] focus:ring-[#136bce] cursor-pointer"
                    checked={po.skus ? selectedSkus.length === po.skus.length : false}
                    onChange={toggleAllSkus}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">SKU Number</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Product Category</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Vendor Code</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Description</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Assigned To</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Inspected By</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Inspection Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Packs</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Qty per Pack</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Total Qty</th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {po.skus?.map((sku) => (
                <tr key={sku.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-[#136bce] focus:ring-[#136bce] cursor-pointer"
                      checked={selectedSkus.includes(sku.id)}
                      onChange={() => toggleSkuSelection(sku.id)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap"><span className="text-sm font-semibold text-slate-900">{sku.sku_number}</span></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{sku.product_category || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{sku.vendor_code || '-'}</td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-900 font-medium">{sku.description || '-'}</div>
                    {(sku.color || sku.size) && (
                      <div className="text-xs text-slate-500">
                        {[sku.color && `Color: ${sku.color}`, sku.size && `Size: ${sku.size}`].filter(Boolean).join(' | ')}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getSkuAssignedTo(sku.id) === 'Unassigned' ? 'text-slate-400 italic' : 'text-slate-700'}`}>
                      {getSkuAssignedTo(sku.id)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {sku.last_inspection ? (
                      <span className="text-sm font-medium text-slate-700">{sku.last_inspection.inspector_name || '-'}</span>
                    ) : (
                      <span className="text-sm text-slate-400 italic">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {sku.last_inspection ? (
                      <span className="text-sm text-slate-600">{formatDateTime(sku.last_inspection.completed_at)}</span>
                    ) : (
                      <span className="text-sm text-slate-400 italic">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    {sku.qty_per_pack > 0 ? (sku.total_qty / sku.qty_per_pack).toFixed(2).replace(/\.?0+$/, '') : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{sku.qty_per_pack}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-1 text-sm font-medium text-slate-700">{sku.total_qty}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openHistory(sku)}
                        disabled={!sku.last_inspection}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title={sku.last_inspection ? 'Inspection history' : 'No inspections yet'}
                      >
                        <span className="material-symbols-outlined text-[16px]">history</span>
                        History
                      </button>
                      <button
                        onClick={() => sku.last_inspection && navigate(`/final-inspection-report/${sku.last_inspection.inspection_id}?sku=${sku.id}`)}
                        disabled={!sku.last_inspection}
                        className="inline-flex items-center gap-1 rounded-lg bg-[#136bce] px-2.5 py-1.5 text-xs font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title={sku.last_inspection ? 'View inspection report' : 'No report yet'}
                      >
                        <span className="material-symbols-outlined text-[16px]">description</span>
                        View Report
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {po.skus && po.skus.length > 0 && (
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <td colSpan={8}></td>
                  <td className="px-6 py-3 text-sm font-bold text-slate-700">
                    Total: {po.skus.reduce((sum, s) => sum + (s.qty_per_pack > 0 ? s.total_qty / s.qty_per_pack : 0), 0).toFixed(2).replace(/\.?0+$/, '')} Packs
                  </td>
                  <td colSpan={1}></td>
                  <td className="px-6 py-3 text-sm font-bold text-slate-900">
                    {po.skus.reduce((sum, s) => sum + s.total_qty, 0).toLocaleString()} Units
                  </td>
                  <td colSpan={1}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="border-t border-slate-200 px-6 py-3 bg-slate-50 rounded-b-xl flex justify-end">
          <p className="text-xs text-slate-400 italic">Showing all SKUs for this Purchase Order</p>
        </div>
      </div>

      {/* Assign PO Modal */}
      {isAssignModalOpen && (
        <div aria-labelledby="modal-title" aria-modal="true" className="fixed inset-0 z-50 overflow-y-auto" role="dialog">
          <div
            aria-hidden="true"
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setIsAssignModalOpen(false)}
          ></div>
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-xl border border-slate-100">
              <button
                className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 transition-colors z-10 p-1 rounded-full hover:bg-slate-100"
                type="button"
                onClick={() => setIsAssignModalOpen(false)}
              >
                <span className="material-symbols-outlined text-[24px]">close</span>
              </button>
              <div className="p-8">
                <div className="mb-8 border-b border-slate-100 pb-6">
                  <div className="flex items-center gap-5">
                    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-[#136bce] ring-1 ring-blue-100 shadow-sm">
                      <span className="material-symbols-outlined text-[28px]">assignment_ind</span>
                    </div>
                    <div className="pt-1">
                      <h3 className="text-xl font-bold leading-6 text-slate-900 tracking-tight" id="modal-title">Assign Purchase Order</h3>
                      <p className="text-sm text-slate-500 mt-1.5">Assign an inspector to selected SKUs for <span className="font-semibold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">{po.po_number}</span>.</p>
                    </div>
                  </div>
                </div>

                {assignError && (
                  <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[16px]">error</span>
                    {assignError}
                  </div>
                )}

                <form className="space-y-6" onSubmit={handleAssign}>
                  <div className="grid grid-cols-1 gap-6">
                    {/* Selected SKUs List */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Selected SKUs ({selectedSkus.length})
                      </label>
                      <div className="flex flex-wrap gap-2 p-3 rounded-xl border border-slate-200 bg-slate-50 max-h-32 overflow-y-auto">
                        {selectedSkus.length > 0 ? (
                          selectedSkus.map(skuId => {
                            const sku = po.skus?.find(s => s.id === skuId);
                            return (
                              <span key={skuId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-100 text-blue-700 text-xs font-bold ring-1 ring-blue-200">
                                {sku?.sku_number || skuId}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-xs text-slate-400 italic">No SKUs selected</span>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        QA Inspector <span className="text-red-500">*</span>
                      </label>
                      {/* Hidden input for form validation */}
                      <input type="hidden" required value={assignForm.user_id} />
                      <div className="relative" ref={inspectorDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsInspectorDropdownOpen(prev => !prev)}
                          className="flex items-center gap-2 w-full rounded-xl border-slate-200 bg-white py-3 pl-3.5 pr-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 hover:bg-slate-50/50 sm:text-sm sm:leading-6 transition-all cursor-pointer text-left"
                        >
                          {selectedInspector ? (
                            selectedInspector.profileImage ? (
                              <img src={selectedInspector.profileImage} alt={selectedInspector.name} className="h-6 w-6 rounded-full object-cover border border-slate-200 shrink-0" />
                            ) : (
                              <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${getAvatarColor(selectedInspector.name)}`}>
                                {getInitials(selectedInspector.name)}
                              </span>
                            )
                          ) : (
                            <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                              <span className="material-symbols-outlined text-[14px] text-slate-400">person_search</span>
                            </span>
                          )}
                          <span className={`truncate ${selectedInspector ? 'text-slate-900' : 'text-slate-400'}`}>
                            {selectedInspector ? selectedInspector.name : 'Choose an inspector...'}
                          </span>
                          <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[20px] text-slate-400">expand_more</span>
                        </button>
                        {isInspectorDropdownOpen && (
                          <div className="absolute left-0 top-full mt-1 z-20 w-full rounded-xl border border-slate-200 bg-white shadow-lg py-1 max-h-60 overflow-y-auto">
                            {nonAdminUsers.map(u => (
                              <button
                                key={u.id}
                                type="button"
                                onClick={() => { setAssignForm(f => ({ ...f, user_id: u.id })); setIsInspectorDropdownOpen(false); }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${assignForm.user_id === u.id ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                              >
                                {u.profileImage ? (
                                  <img src={u.profileImage} alt={u.name} className="h-6 w-6 rounded-full object-cover border border-slate-200 shrink-0" />
                                ) : (
                                  <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${getAvatarColor(u.name)}`}>
                                    {getInitials(u.name)}
                                  </span>
                                )}
                                {u.name}
                              </button>
                            ))}
                            {nonAdminUsers.length === 0 && (
                              <p className="px-3 py-2 text-sm text-slate-400 italic">No inspectors available</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="inspection-type">
                        Inspection Type <span className="text-red-500">*</span>
                      </label>
                      {/* Hidden input for form validation */}
                      <input type="hidden" required value={assignForm.inspection_type} />
                      <div className="relative" ref={inspectionTypeDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsInspectionTypeDropdownOpen(prev => !prev)}
                          className="flex items-center gap-2 w-full rounded-xl border-slate-200 bg-white py-3 pl-3.5 pr-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 hover:bg-slate-50/50 sm:text-sm sm:leading-6 transition-all cursor-pointer text-left"
                        >
                          <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                            <span className="material-symbols-outlined text-[14px] text-slate-400">fact_check</span>
                          </span>
                          <span className={`truncate ${selectedInspectionType ? 'text-slate-900' : 'text-slate-400'}`}>
                            {selectedInspectionType ? selectedInspectionType.label : 'Select inspection type...'}
                          </span>
                          <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[20px] text-slate-400">expand_more</span>
                        </button>
                        {isInspectionTypeDropdownOpen && (
                          <div className="absolute left-0 top-full mt-1 z-20 w-full rounded-xl border border-slate-200 bg-white shadow-lg py-1 max-h-60 overflow-y-auto">
                            {inspectionTypeOptions.map(o => (
                              <button
                                key={o.value}
                                type="button"
                                onClick={() => { setAssignForm(f => ({ ...f, inspection_type: o.value })); setIsInspectionTypeDropdownOpen(false); }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${assignForm.inspection_type === o.value ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                              >
                                <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                  <span className="material-symbols-outlined text-[14px] text-slate-400">fact_check</span>
                                </span>
                                {o.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="due-date">
                        Due Date
                      </label>
                      <DatePicker
                        value={assignForm.due_date}
                        onChange={(val) => setAssignForm(f => ({ ...f, due_date: val }))}
                        placeholder="Select due date"
                        dropUp
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="instructions">
                        Special Instructions
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute top-3 left-0 flex items-center pl-3.5">
                          <span className="material-symbols-outlined text-slate-400 text-[20px]">notes</span>
                        </div>
                        <textarea
                          className="block w-full rounded-xl border-slate-200 bg-white py-3 pl-11 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-[#136bce] focus:border-[#136bce] sm:text-sm sm:leading-6 transition-all resize-none hover:bg-slate-50/50"
                          id="instructions"
                          name="instructions"
                          placeholder="Enter specific inspection guidelines or notes..."
                          rows={3}
                          value={assignForm.special_instructions}
                          onChange={(e) => setAssignForm(f => ({ ...f, special_instructions: e.target.value }))}
                        ></textarea>
                      </div>
                    </div>
                    <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-slate-100">
                      <button
                        className="inline-flex w-full justify-center items-center rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900 sm:w-auto transition-all"
                        type="button"
                        onClick={() => setIsAssignModalOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="inline-flex w-full justify-center items-center rounded-xl bg-[#136bce] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 hover:bg-blue-600 sm:w-auto transition-all disabled:opacity-50"
                        type="submit"
                        disabled={assigning || selectedSkus.length === 0}
                      >
                        {assigning ? (
                          <span className="material-symbols-outlined animate-spin text-[20px] mr-2">progress_activity</span>
                        ) : (
                          <span className="material-symbols-outlined text-[20px] mr-2">check_circle</span>
                        )}
                        {assigning ? 'Assigning...' : 'Assign'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inspection History Modal */}
      {historySku && (
        <div aria-modal="true" className="fixed inset-0 z-50 overflow-y-auto" role="dialog">
          <div
            aria-hidden="true"
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setHistorySku(null)}
          ></div>
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-2xl border border-slate-100">
              <button
                className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 transition-colors z-10 p-1 rounded-full hover:bg-slate-100"
                type="button"
                onClick={() => setHistorySku(null)}
              >
                <span className="material-symbols-outlined text-[24px]">close</span>
              </button>
              <div className="p-8">
                <div className="mb-6 border-b border-slate-100 pb-5">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#136bce] ring-1 ring-blue-100 shadow-sm">
                      <span className="material-symbols-outlined text-[24px]">history</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold leading-6 text-slate-900 tracking-tight">Inspection History</h3>
                      <p className="text-sm text-slate-500 mt-1">Final inspections for <span className="font-semibold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">{historySku.sku_number}</span></p>
                    </div>
                  </div>
                </div>

                {historyLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <span className="material-symbols-outlined animate-spin text-[24px] text-slate-400">progress_activity</span>
                    <span className="ml-2 text-slate-500">Loading...</span>
                  </div>
                ) : history.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <span className="material-symbols-outlined text-[40px] mb-2">inventory</span>
                    <p className="text-sm">No inspection history yet.</p>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-xl border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Inspected By</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Date &amp; Time</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Report</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {history.map((item, idx) => (
                          <tr key={`${item.inspection_id}-${idx}`} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900">{item.inspector_name || '-'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">{formatDateTime(item.date)}</td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${item.status === 'pass' ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' : 'bg-red-50 text-red-700 ring-red-600/20'}`}>
                                {item.status === 'pass' ? 'Pass' : 'Fail'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <button
                                onClick={() => navigate(`/final-inspection-report/${item.inspection_id}?sku=${historySku.id}`)}
                                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-[#136bce] hover:bg-blue-50 transition-colors"
                                title="View report"
                              >
                                <span className="material-symbols-outlined text-[16px]">description</span>
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PODetails;
